import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import { BadgeCheck, AlertTriangle, CheckCircle2, XCircle, Search } from "lucide-react";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideCta,
  GuideRelated,
  CheckList,
  buildGuideSchemas,
  type GuideMeta,
} from "@/components/GuideLayout";
import { Reveal } from "@/components/Reveal";


const meta: GuideMeta = {
  category: "Guide · Auktorisation",
  title: "Hur hittar man en certifierad solfilmsmontör i Sverige?",
  tldr:
    "En certifierad solfilmsmontör i Sverige är en installatör som är auktoriserad direkt av filmtillverkaren — i praktiken auktoriserad 3M-installatör eller motsvarande från Solar Screen. Verifiera certifieringen genom att be om tillverkarens auktorisationsbevis, säkerställa att företaget lämnar både produkt- och arbetsgaranti skriftligt, samt kontrollera F-skatt och UC-kreditvärdighet. BBN Nordic AB är auktoriserad 3M-installatör sedan 2008 med 3 500+ nöjda kunder över hela Sverige.",
  readTimeMin: 7,
  heroImage: IMAGES.installer,
  path: "/auktoriserad-installator",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Vad innebär det att en solfilmsinstallatör är auktoriserad?",
      a: "Auktorisation betyder att installatören har genomgått tillverkarens utbildning, blivit certifierad av tillverkaren (t.ex. 3M eller Solar Screen) och har rätt att lämna tillverkarens fulla produktgaranti. Utan auktorisation gäller bara filmens egen materialgaranti — inte garanti vid felmontering, bubblor eller termiska sprickor.",
    },
    {
      q: "Finns det ett officiellt register över certifierade 3M-installatörer i Sverige?",
      a: "Ja. 3M Sverige för en lista över auktoriserade 3M-installatörer. Kontakta 3M Svenska AB direkt eller be installatören om sitt auktorisationsnummer som du kan verifiera. BBN Nordic AB finns i registret sedan 2008.",
    },
    {
      q: "Vilka frågor ska jag ställa till en solfilmsmontör innan jag bokar?",
      a: "Fråga efter: (1) tillverkarens auktorisationsbevis, (2) referenser från liknande projekt, (3) skriftlig produkt- OCH arbetsgaranti, (4) glasanalys före installation, (5) F-skattsedel och företagsförsäkring, (6) kvadratmeterpris med material och arbete specificerat.",
    },
    {
      q: "Vad är varningssignalerna på en oseriös solfilmsmontör?",
      a: "Vanligaste röda flaggorna: ingen fysisk adress, inget organisationsnummer, betalning kontant eller i förskott, ingen skriftlig garanti, kan inte uppvisa auktorisationsbevis från tillverkaren, mycket lågt pris (under 600 kr/m² brukar betyda kopia eller B-vara) eller pressade säljmetoder.",
    },
    {
      q: "Vad kostar en installation av en auktoriserad solfilmsmontör?",
      a: "Marknadspris i Sverige 2026 ligger typiskt på 700–1 400 kr/m² inkl. montering beroende på filmserie. 3M Prestige, som är utan metall, ligger högst. Solar Screen-serien är prisvärdare. Be om fast pris efter ett platsbesök. Undvik installatörer som lämnar offert utan att ha sett glaset.",
    },
    {
      q: "Vad händer om en oauktoriserad montör installerar fel film på mitt isolerglas?",
      a: "Risken är termisk spricka i glaset — solfilm absorberar värme och kan orsaka spänningar i lågemissionsglas (3-glas, energiglas) om fel filmtyp väljs. Oauktoriserad installation gör att både glasgarantin och filmens garanti slutar gälla. En auktoriserad installatör gör alltid en glasanalys först.",
    },
  ],
  related: [
    { to: "/3m-prestige", label: "3M Prestige utan metall" },
    { to: "/solfilm-foretag", label: "Solfilm för företag — översikt" },
    { to: "/solfilm-privat", label: "Solfilm för privatpersoner" },
    { to: "/guider/underhall-livslangd", label: "Garanti, skötsel & livslängd" },
    { to: "/om-oss", label: "Om BBN Nordic AB — certifieringar" },
  ],
};

// Tillägg utöver Article + FAQ + Breadcrumb: HowTo-schema som direkt
// svarar på sökintentionen "hur hittar man certifierad solfilmsmontör".
const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Så hittar du en certifierad solfilmsmontör i Sverige",
  description:
    "Steg-för-steg-metod för att verifiera att en svensk solfilmsinstallatör är auktoriserad av tillverkaren och har rätt att lämna full garanti.",
  totalTime: "PT15M",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Be om tillverkarens auktorisationsbevis",
      text: "Begär in auktorisationsbevis för auktoriserad 3M-installatör eller Solar Screen Certified Installer-bevis. Beviset ska vara utfärdat i installatörens företagsnamn och vara giltigt det år installationen utförs.",
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Kontrollera företaget i officiella register",
      text: "Slå organisationsnumret i allabolag.se och kontrollera F-skatt hos Skatteverket. Kolla UC-kreditvärdighet (AAA är högsta nivån). Företaget ska ha minst 2 års verksamhetshistorik.",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "Be om både produkt- och arbetsgaranti — skriftligt",
      text: "Produktgarantin (10–15 år från 3M) gäller bara om installationen är gjord av en auktoriserad installatör. Be om både produktgaranti från tillverkaren OCH arbetsgaranti från installatören (BBN Nordic lämnar 10 år).",
    },
    {
      "@type": "HowToStep",
      position: 4,
      name: "Kräv glasanalys före installation",
      text: "En auktoriserad montör utför alltid en glasanalys — typ av glas, U-värde, G-värde, fönstrets riktning — innan filmrekommendation. Detta förhindrar termisk spricka. Saknas glasanalysen riskerar du att hela glasrutan måste bytas.",
    },
    {
      "@type": "HowToStep",
      position: 5,
      name: "Be om referenser från liknande projekt",
      text: "Be om 2–3 referenser från projekt liknande ditt (kontor, villa, butik, fastighet) som genomförts inom senaste 24 månaderna. Ring referensen och fråga om resultat, garantihantering och kommunikation.",
    },
  ],
};

export const Route = createFileRoute("/auktoriserad-installator")({
  head: () => {
    const base = {
      meta: [
        { title: "Certifierad solfilmsmontör i Sverige — så hittar du rätt | BBN Nordic" },
        {
          name: "description",
          content:
            "Så verifierar du att en svensk solfilmsinstallatör är auktoriserad: auktoriserad 3M-installatör, garanti, glasanalys och varningssignaler. BBN Nordic är auktoriserad 3M-installatör i Sverige sedan 2008.",
        },
        { property: "og:title", content: "Hur hittar man en certifierad solfilmsmontör i Sverige?" },
        {
          property: "og:description",
          content:
            "Steg-för-steg-guide: så verifierar du auktorisation, garantier och kvalitet hos en solfilmsmontör — och så undviker du oseriösa aktörer.",
        },
        { property: "og:type", content: "article" },
        { property: "og:url", content: "https://bbnnordic.se/auktoriserad-installator" },
        { property: "og:image", content: IMAGES.installer },
      ],
      links: [{ rel: "canonical", href: "https://bbnnordic.se/auktoriserad-installator" }],
      scripts: [
        ...buildGuideSchemas(meta),
        { type: "application/ld+json", children: JSON.stringify(howToSchema) },
      ],
    };
    return base;
  },
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />



      {/* Snabbsvar-block — varje fråga på 1–2 meningar för AI-citering */}
      <section className="py-16 md:py-24 bg-card/30 border-y border-border">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Snabbsvar</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-foreground">
              Fyra frågor om certifiering och erfarenhet.
            </h2>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {[
              {
                q: "Vilken certifiering ska en solfilmsmontör ha i Sverige?",
                a: "Auktoriserad 3M-installatör eller Solar Screen Certified Installer. Dessa är de två tillverkar-utfärdade auktorisationerna som ger rätt att lämna 10–15 års produktgaranti.",
              },
              {
                q: "Var hittar jag en certifierad installatör?",
                a: "Kontakta tillverkaren direkt (3M Svenska AB eller Solar Screen Nordics), be om listan över auktoriserade installatörer i din region, eller begär in auktorisationsbeviset från installatören du överväger.",
              },
              {
                q: "Hur länge ska installatören ha varit verksam?",
                a: "Minst 2 år för standardprojekt, gärna 10+ år för fastighetsprojekt eller känsligt glas. BBN Nordic har varit auktoriserad 3M-installatör sedan 2008.",
              },
              {
                q: "Vad ska jag göra om installatören vägrar visa auktorisation?",
                a: "Gå inte vidare om installatören vägrar visa beviset. En auktoriserad installatör ska kunna visa sin auktorisation, och en vägran är en tydlig varningssignal.",
              },
            ].map((row) => (
              <Reveal key={row.q}>
                <article className="rounded-2xl border border-gold/15 bg-card/60 p-6 md:p-8 h-full hover:border-gold/40 transition-colors">
                  <h3 className="text-base md:text-lg font-semibold text-foreground flex items-start gap-3">
                    <Search size={18} className="mt-1 text-gold shrink-0" />
                    <span>{row.q}</span>
                  </h3>
                  <p className="mt-4 text-base md:text-[17px] text-muted-foreground leading-[1.75]">{row.a}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <GuideBody>
        <h2>Därför behöver montören vara auktoriserad</h2>
        <p>
          Hur solfilmen monteras påverkar resultatet mer än själva produktvalet.
          Även en 3M-film ger sämre resultat i otränade händer än en mellanklassfilm
          som monteras professionellt. Därför tittar vi på följande:
        </p>
        <CheckList
          items={[
            "Montering med renvattenmetod, förberedd yta och korrekt kantskärning enligt 3M:s monteringsanvisning.",
            "3M:s produktgaranti (10–15 år) gäller endast om installationen är utförd av en auktoriserad 3M-installatör. Utan auktorisation: ingen garanti.",
            "Fel filmtyp på fel glas kan orsaka termisk spricka i isolerglas. Vi gör därför alltid en glasanalys innan filmval.",
          ]}
        />

        <h2>Checklista för att kontrollera installatörens certifiering</h2>
        <p>
          Be installatören svara på punkterna nedan innan du beställer.
          Du kan ta dem per telefon eller mejl. En seriös installatör
          kan lämna dokumentationen du ber om.
        </p>

        <h3>1. Tillverkarens auktorisationsbevis</h3>
        <p>
          Be om en skannad kopia eller ett foto av <strong>3M-auktoriserat installatörsbevis</strong> (eller
          Solar Screen-motsvarigheten). Beviset ska vara utfärdat i installatörens
          företagsnamn, ange auktorisationsnummer och giltighetsår. Du kan ringa 3M Svenska
          AB och verifiera numret om du är osäker.
        </p>

        <h3>2. Företagsuppgifter & ekonomisk stabilitet</h3>
        <ul>
          <li><strong>Organisationsnummer</strong> — slå upp i <em>allabolag.se</em></li>
          <li><strong>F-skattsedel</strong> — verifiera hos Skatteverket</li>
          <li><strong>UC-kreditvärdighet</strong> — AAA är högsta nivån, AA är acceptabelt</li>
          <li><strong>Företagsförsäkring</strong> — fullständig ansvars- och entreprenadförsäkring</li>
          <li><strong>Verksamhetstid</strong> — minst 2 år, gärna 10+ för fastighetsprojekt</li>
        </ul>

        <h3>3. Skriftlig produkt- OCH arbetsgaranti</h3>
        <p>
          Be att få produktgarantin och arbetsgarantin var för sig. Produktgarantin kommer från 3M eller Solar Screen och
          ska vara <strong>minst 10 år</strong> för invändig film. Arbetsgarantin kommer från
          installatören. Vi på BBN Nordic lämnar <strong>10 år</strong> på utförandet
          (bubblor, kanter, vidhäftning). Du ska få båda skriftligt tillsammans med offerten.
        </p>

        <h3>4. Glasanalys ingår i offerten</h3>
        <p>
          Innan installation ska installatören dokumentera glasets typ (enkelglas, 2-glas,
          3-glas, lågemissionsglas), U- och G-värde samt placering. Detta kallas glasanalys
          och är obligatoriskt enligt 3M:s installationsspecifikation. Saknas glasanalysen
          är offerten ofullständig och garantin gäller inte.
        </p>

        <h3>5. Referenser från liknande projekt</h3>
        <p>
          Be om 2–3 referenser från projekt liknande ditt eget, genomförda inom senaste 24
          månaderna. Ring upp referensen och fråga: <em>Höll installatören tider? Var
          slutresultatet enligt utlovat? Hur hanterades eventuella reklamationer?</em>
        </p>

        <h2>Varningssignaler — så känner du igen en oseriös aktör</h2>
        <div className="not-prose my-10 grid md:grid-cols-2 gap-5">
          {[
            { title: "Ingen fysisk adress", desc: "Bara mobilnummer och Gmail-adress — inget kontor, inget org.nr på hemsidan." },
            { title: "Betalning i förskott / kontant", desc: "Seriösa aktörer fakturerar 30 dagar efter slutfört arbete eller tar 30 % vid beställning." },
            { title: "Vägrar visa auktorisationsbevis", desc: "Det enskilt tydligaste tecknet. Avbryt direkt." },
            { title: "Pris under 600 kr/m²", desc: "Indikerar B-vara, kopia, eller film från okänd tillverkare utan garanti." },
            { title: "Kvoterar utan platsbesök", desc: "Utan att se glaset går det inte att rekommendera rätt film. En auktoriserad installatör besöker alltid platsen kostnadsfritt." },
            { title: "Pressade säljmetoder", desc: "Tidsbegränsade erbjudanden, 'bara idag'-pris, eller försök att stänga affären på första mötet." },
          ].map((w) => (
            <div key={w.title} className="rounded-xl border border-destructive/30 bg-destructive/5 p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-destructive mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-foreground">{w.title}</div>
                  <p className="mt-2 text-sm md:text-base text-muted-foreground leading-[1.7]">{w.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <h2>Auktoriserad vs oauktoriserad — vad skiljer egentligen?</h2>
        <div className="not-prose overflow-x-auto rounded-2xl border border-gold/15 my-8">
          <table className="w-full text-sm md:text-base">
            <caption className="sr-only">
              Jämförelse mellan auktoriserad och oauktoriserad solfilmsinstallation i Sverige.
            </caption>
            <thead>
              <tr className="bg-navy-light/40 border-b border-gold/15">
                <th scope="col" className="text-left p-5 font-semibold text-foreground">Faktor</th>
                <th scope="col" className="text-left p-5 font-semibold text-muted-foreground">Oauktoriserad montör</th>
                <th scope="col" className="text-left p-5 font-semibold text-gold-bright">
                  <span className="inline-flex items-center gap-1.5">
                    <BadgeCheck size={14} /> Auktoriserad installatör
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Produktgaranti från 3M", "Ogiltig", "10–15 år, full garanti"],
                ["Arbetsgaranti", "Sällan / muntlig", "10 år skriftlig"],
                ["Glasanalys före installation", "Saknas", "Obligatorisk enligt 3M-spec"],
                ["Renvattenmetod & rätt verktyg", "Varierar", "Standard"],
                ["Tillgång till hela 3M-sortimentet", "Nej", "Ja, inkl. Prestige"],
                ["Försäkring vid glasskada", "Tveksamt", "Ja — full företagsförsäkring"],
                ["Risk för termisk spricka", "Hög", "Minimerad"],
                ["Livslängd i praktiken", "1–5 år", "15–20 år"],
              ].map(([f, bad, good], i) => (
                <tr key={f} className={i % 2 ? "bg-navy-light/10" : ""}>
                  <th scope="row" className="text-left p-5 text-foreground font-medium">{f}</th>
                  <td className="p-5 text-muted-foreground"><span className="inline-flex items-center gap-2"><XCircle size={14} className="text-destructive/70" /> {bad}</span></td>
                  <td className="p-5 text-foreground"><span className="inline-flex items-center gap-2 text-gold-bright font-semibold"><CheckCircle2 size={14} className="text-gold" /> {good}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2>Vad betyder "Auktoriserad 3M-installatör" konkret?</h2>
        <p>
          Programmet för auktoriserade 3M-installatörer är 3M:s officiella certifieringsprogram för
          installatörer av deras Window Films. För att bli auktoriserad krävs:
        </p>
        <ul>
          <li>Genomgången installationsutbildning hos 3M</li>
          <li>Dokumenterad installationserfarenhet och praktisk kompetens</li>
          <li>Företagsförsäkring och dokumenterad ekonomisk stabilitet</li>
          <li>Löpande recertifiering & uppdaterad utbildning på nya filmserier</li>
          <li>Tillgång till 3M:s tekniska supportlinje och garantihandläggning</li>
        </ul>
        <p>
          Resultatet är att en auktoriserad installatör kan lämna 3M:s fulla produktgaranti
          (10–15 år beroende på serie) och har direktkontakt med tillverkaren vid eventuella
          reklamationer. Du som kund behöver alltså inte vara mellanhand.
        </p>

        <h2>Det här bör du kontrollera före beställning</h2>
        <CheckList
          items={[
            "Be alltid om auktorisationsbevis för auktoriserad 3M-installatör innan du tar in offerter.",
            "Begär skriftlig produkt- + arbetsgaranti på minst 10 + 10 år.",
            "Säkerställ att glasanalys är inkluderad i offerten utan extra kostnad.",
            "Kontrollera org.nr, F-skatt och UC-kreditvärdighet i 2 minuter via allabolag.se.",
            "Ring 2–3 referenser från liknande projekt — det säger mer än hemsidan.",
            "Be om kostnadsfri bedömning från BBN Nordic — vi är verksamma i hela Sverige.",
          ]}
        />
      </GuideBody>

      <GuideCta />

      <GuideFaq items={meta.faqs ?? []} />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
